/*
 * VoxPont brand geometry + SVG builder — the single source of truth for every
 * VoxPont logo asset (live React mark, exported PNGs, downloadable SVG files).
 *
 * WHAT: Pure data + string builders. No DOM, no React, no Node-only APIs, so the
 *       browser (VoxPontLogo.jsx) and the export script (scripts/build-voxpont-assets.mjs)
 *       both render the *identical* markup and can never drift.
 *
 * CORE CONCEPT: VoxPont = "voice bridge". The symbol is two constant-width half-ring
 *       speech bubbles facing each other "( )" — NOT crescents — joined by a minimal
 *       symbolic suspension bridge (one center support + two suspension curves).
 *
 * GEOMETRY RULE: each bubble is a half annulus with outer radius R and inner radius
 *       R - T, so the wall thickness T is constant through the entire arc.
 */

// ---------------------------------------------------------------------------
// Color system (matches the brand prompt exactly).
// ---------------------------------------------------------------------------

export const SATIN_BLUE = {
  highlight: "#234F9A",
  core: "#1B4584",
  midtone: "#153766",
  shadow: "#0E2A4D",
  deep: "#0C1E36",
};

export const ANTIQUE_GOLD = {
  highlight: "#D4AF37",
  core: "#B08A28",
  warm: "#8C6A1A",
  midtone: "#7A5E17",
  shadow: "#4A3607",
  deep: "#3A2A05",
};

export const PLATINUM = {
  white: "#FFFFFF",
  light: "#F4F5F6",
  core: "#D6D9DC",
  midtone: "#9AA0A6",
  shadow: "#848A90",
};

export const BLACK = "#0A0A0A";

// ---------------------------------------------------------------------------
// Symbol geometry. Native symbol viewBox is 200 x 120.
// ---------------------------------------------------------------------------

const SYM = {
  w: 200,
  h: 120,
  cy: 60,
  R: 46, // outer radius
  T: 15, // constant wall thickness
  cxL: 60, // left bubble flat edge (opening faces right)
  cxR: 140, // right bubble flat edge (opening faces left)
};

const n = (v) => Number(v.toFixed(2));

// How far each bubble wraps around its circle. 180 = exact semicircle (flat mouth
// on the vertical diameter); >180 curls the tips inward and narrows the opening.
// Single source of truth so the wrap is one number to tune.
const WRAP_DEG = 190;
const OPEN_HALF = (((360 - WRAP_DEG) / 2) * Math.PI) / 180; // mouth half-angle (rad)
const BIG_ARC = WRAP_DEG > 180 ? 1 : 0;
const COS_H = Math.cos(OPEN_HALF);
const SIN_H = Math.sin(OPEN_HALF);

/** Left speech bubble: constant-width half-ring opening to the right, like "(". */
function leftBubblePath() {
  const { cy, R, T, cxL } = SYM;
  const Ri = R - T;
  // Tips sit at +/- OPEN_HALF from the +x (opening) axis; the arc covers the back.
  return [
    `M${n(cxL + R * COS_H)} ${n(cy - R * SIN_H)}`,
    `A${R} ${R} 0 ${BIG_ARC} 0 ${n(cxL + R * COS_H)} ${n(cy + R * SIN_H)}`,
    `L${n(cxL + Ri * COS_H)} ${n(cy + Ri * SIN_H)}`,
    `A${Ri} ${Ri} 0 ${BIG_ARC} 1 ${n(cxL + Ri * COS_H)} ${n(cy - Ri * SIN_H)}`,
    "Z",
  ].join(" ");
}

/** Right speech bubble: constant-width half-ring opening to the left, like ")". */
function rightBubblePath() {
  const { cy, R, T, cxR } = SYM;
  const Ri = R - T;
  // Mirror of the left bubble: tips at +/- OPEN_HALF from the -x (opening) axis.
  return [
    `M${n(cxR - R * COS_H)} ${n(cy - R * SIN_H)}`,
    `A${R} ${R} 0 ${BIG_ARC} 1 ${n(cxR - R * COS_H)} ${n(cy + R * SIN_H)}`,
    `L${n(cxR - Ri * COS_H)} ${n(cy + Ri * SIN_H)}`,
    `A${Ri} ${Ri} 0 ${BIG_ARC} 0 ${n(cxR - Ri * COS_H)} ${n(cy - Ri * SIN_H)}`,
    "Z",
  ].join(" ");
}

/**
 * Small subtle speech tail on the lower-back (outer) of the left bubble, pointing
 * down-and-out. Placed away from the ring opening so it reads as a speech-bubble
 * tail rather than an arrowhead on a circular arc.
 */
function leftTailPath() {
  return "M33 94 L44 103 L26 110 Z";
}

function rightTailPath() {
  // Mirror of the left tail about x = 100.
  return "M167 94 L156 103 L174 110 Z";
}

// Symbolic suspension bridge spanning the negative space between the bubbles:
// a central spire tower, two main cables draping from the tower top down to the
// deck anchors at each bubble mouth, a horizontal deck, and vertical suspender
// hangers from each cable down to the deck. The deck/cables/suspenders are split
// at center so the left half matches the left bubble and the right half the right.
const MID_X = 100;
const DECK_Y = 66; // roadway level (just below the bubble centers)
const MAST_TOP_Y = 16; // spire tip
const CABLE_TOP_Y = 24; // where the two main cables meet the mast
const CABLE_CTRL_Y = 62; // control-point depth that makes the cables drape gracefully

// Quadratic Bezier sample, used to hang the suspenders off the main cable.
const bezQ = (p0, p1, p2, t) => {
  const mt = 1 - t;
  return [
    mt * mt * p0[0] + 2 * mt * t * p1[0] + t * t * p2[0],
    mt * mt * p0[1] + 2 * mt * t * p1[1] + t * t * p2[1],
  ];
};

const SUSPENDER_TS = [0.24, 0.44, 0.64, 0.84];

const CABLE_L = [[SYM.cxL, DECK_Y], [80, CABLE_CTRL_Y], [MID_X, CABLE_TOP_Y]];
const CABLE_R = [[SYM.cxR, DECK_Y], [120, CABLE_CTRL_Y], [MID_X, CABLE_TOP_Y]];

function suspendersPath([p0, p1, p2]) {
  return SUSPENDER_TS.map((t) => {
    const [x, y] = bezQ(p0, p1, p2, t);
    return `M${n(x)} ${n(y)} L${n(x)} ${DECK_Y}`;
  }).join(" ");
}

const cablePath = ([p0, p1, p2]) =>
  `M${n(p0[0])} ${n(p0[1])} Q${n(p1[0])} ${n(p1[1])} ${n(p2[0])} ${n(p2[1])}`;

const BRIDGE = {
  deckLeft: `M${SYM.cxL} ${DECK_Y} L${MID_X} ${DECK_Y}`,
  deckRight: `M${MID_X} ${DECK_Y} L${SYM.cxR} ${DECK_Y}`,
  tower: `M${MID_X} ${MAST_TOP_Y} L${MID_X} ${DECK_Y}`,
  cableLeft: cablePath(CABLE_L),
  cableRight: cablePath(CABLE_R),
  suspendersLeft: suspendersPath(CABLE_L),
  suspendersRight: suspendersPath(CABLE_R),
};

export const SYMBOL = {
  viewBox: `0 0 ${SYM.w} ${SYM.h}`,
  width: SYM.w,
  height: SYM.h,
  leftBubble: leftBubblePath(),
  rightBubble: rightBubblePath(),
  leftTail: leftTailPath(),
  rightTail: rightTailPath(),
  bridge: BRIDGE,
};

// ---------------------------------------------------------------------------
// Tone resolution. A "tone" maps the three logical ink slots
// (left bubble, right bubble, bridge, wordmark-vox, wordmark-pont, tagline)
// to concrete fills/gradient refs.
// ---------------------------------------------------------------------------

/**
 * The bridge is split: its left half (cableLeft) takes bridgeLeft, which matches
 * the left voice bubble, while the right half (cableRight + center support) takes
 * bridgeRight, matching the right bubble.
 *
 * @param {string} tone - color | ondark | white | blue | black | gold
 * @param {string} idp - unique id prefix for gradients
 */
function resolveInk(tone, idp) {
  switch (tone) {
    case "ondark":
      // For dark surfaces: satin-platinum left half + gold right half, keeping the
      // VoxPont split legible (the satin-blue gradient disappears on navy). Bridge
      // strokes are solid tones — a vertical gradient is degenerate on the flat deck.
      return {
        bubbleL: `url(#${idp}-plat)`,
        bubbleR: `url(#${idp}-gold)`,
        bridgeLeft: PLATINUM.core,
        bridgeRight: ANTIQUE_GOLD.core,
        vox: `url(#${idp}-plat)`,
        pont: `url(#${idp}-gold)`,
        tagline: PLATINUM.core,
      };
    case "white":
      return { bubbleL: "#FFFFFF", bubbleR: "#FFFFFF", bridgeLeft: "#FFFFFF", bridgeRight: "#FFFFFF", vox: "#FFFFFF", pont: "#FFFFFF", tagline: "#FFFFFF" };
    case "blue":
      return { bubbleL: SATIN_BLUE.core, bubbleR: SATIN_BLUE.core, bridgeLeft: SATIN_BLUE.core, bridgeRight: SATIN_BLUE.core, vox: SATIN_BLUE.core, pont: SATIN_BLUE.core, tagline: SATIN_BLUE.core };
    case "black":
      return { bubbleL: BLACK, bubbleR: BLACK, bridgeLeft: BLACK, bridgeRight: BLACK, vox: BLACK, pont: BLACK, tagline: BLACK };
    case "gold":
      return { bubbleL: ANTIQUE_GOLD.core, bubbleR: ANTIQUE_GOLD.core, bridgeLeft: ANTIQUE_GOLD.core, bridgeRight: ANTIQUE_GOLD.core, vox: ANTIQUE_GOLD.core, pont: ANTIQUE_GOLD.core, tagline: ANTIQUE_GOLD.core };
    case "color":
    default:
      // Bubbles keep their satin metal gradients; the thin bridge strokes use solid
      // brand-core tones so the deck/cables/suspenders read as one uniform color per
      // side (a vertical gradient is degenerate on the flat horizontal deck stroke).
      return {
        bubbleL: `url(#${idp}-blue)`,
        bubbleR: `url(#${idp}-gold)`,
        bridgeLeft: SATIN_BLUE.core,
        bridgeRight: ANTIQUE_GOLD.core,
        vox: `url(#${idp}-blue)`,
        pont: `url(#${idp}-gold)`,
        tagline: `url(#${idp}-plat)`,
      };
  }
}

function gradientDefs(idp) {
  return `
    <linearGradient id="${idp}-blue" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${SATIN_BLUE.highlight}"/>
      <stop offset="38%" stop-color="${SATIN_BLUE.core}"/>
      <stop offset="72%" stop-color="${SATIN_BLUE.midtone}"/>
      <stop offset="100%" stop-color="${SATIN_BLUE.shadow}"/>
    </linearGradient>
    <linearGradient id="${idp}-gold" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${ANTIQUE_GOLD.highlight}"/>
      <stop offset="32%" stop-color="${ANTIQUE_GOLD.core}"/>
      <stop offset="62%" stop-color="${ANTIQUE_GOLD.warm}"/>
      <stop offset="84%" stop-color="${ANTIQUE_GOLD.midtone}"/>
      <stop offset="100%" stop-color="${ANTIQUE_GOLD.shadow}"/>
    </linearGradient>
    <linearGradient id="${idp}-plat" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${PLATINUM.light}"/>
      <stop offset="55%" stop-color="${PLATINUM.core}"/>
      <stop offset="100%" stop-color="${PLATINUM.midtone}"/>
    </linearGradient>`;
}

// ---------------------------------------------------------------------------
// Symbol + wordmark fragment builders.
// ---------------------------------------------------------------------------

function symbolGroup(ink, { withTails = true } = {}) {
  const tails = withTails
    ? `
      <path d="${SYMBOL.leftTail}" fill="${ink.bubbleL}"/>
      <path d="${SYMBOL.rightTail}" fill="${ink.bubbleR}"/>`
    : "";
  return `
    <g class="vp-symbol" fill="none">
      <path class="vp-bubble-left" d="${SYMBOL.leftBubble}" fill="${ink.bubbleL}"/>
      <path class="vp-bubble-right" d="${SYMBOL.rightBubble}" fill="${ink.bubbleR}"/>${tails}
      <g class="vp-bridge" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g class="vp-bridge-left" stroke="${ink.bridgeLeft}">
          <path d="${SYMBOL.bridge.deckLeft}" stroke-width="3.5"/>
          <path d="${SYMBOL.bridge.cableLeft}" stroke-width="2.5"/>
          <path d="${SYMBOL.bridge.suspendersLeft}" stroke-width="1.3"/>
        </g>
        <g class="vp-bridge-right" stroke="${ink.bridgeRight}">
          <path d="${SYMBOL.bridge.deckRight}" stroke-width="3.5"/>
          <path d="${SYMBOL.bridge.cableRight}" stroke-width="2.5"/>
          <path d="${SYMBOL.bridge.suspendersRight}" stroke-width="1.3"/>
        </g>
        <path class="vp-bridge-tower" d="${SYMBOL.bridge.tower}" stroke="${ink.bridgeRight}" stroke-width="3.5"/>
      </g>
    </g>`;
}

/**
 * Wordmark "VoxPont" as editable Federo text. The two color halves are flowing
 * <tspan>s, so "Pont" is auto-kerned right after "Vox" by the font itself (no
 * manual advance measurement). Because PNG export feeds the real Federo font to
 * the rasterizer, we keep this as live <text> (editable, no outlines).
 *
 * @param {string} [anchor=start] - start | middle (middle centers VoxPont on x)
 */
function wordmarkGroup(ink, { x, y, size, anchor = "start" }) {
  return `
    <text class="vp-wordmark" x="${n(x)}" y="${n(y)}" font-family="Federo, 'Times New Roman', serif" font-weight="400" font-size="${size}" letter-spacing="${n(size * 0.01)}" text-anchor="${anchor}"><tspan fill="${ink.vox}">Vox</tspan><tspan fill="${ink.pont}">Pont</tspan></text>`;
}

function taglineGroup(ink, { x, y, size, anchor = "start" }) {
  return `
    <text class="vp-tagline" x="${n(x)}" y="${n(y)}" font-family="Inter, system-ui, sans-serif" font-weight="600" font-size="${size}" letter-spacing="${n(size * 0.22)}" text-anchor="${anchor}" fill="${ink.tagline}">REAL-TIME TRANSLATION SERVICE</text>`;
}

// ---------------------------------------------------------------------------
// Lockup configuration. Each variant declares its own viewBox and composition.
// ---------------------------------------------------------------------------

export const VARIANTS = {
  symbol: { viewBox: SYMBOL.viewBox },
  wordmark: { viewBox: "0 0 560 150" },
  "wordmark-tagline": { viewBox: "0 0 560 190" },
  primary: { viewBox: "0 0 820 220" },
  "primary-no-tagline": { viewBox: "0 0 800 200" },
  stacked: { viewBox: "0 0 440 370" },
};

/**
 * Build a complete standalone SVG string for a VoxPont lockup.
 *
 * @param {Object} opts
 * @param {string} [opts.variant=primary] - primary | primary-no-tagline | symbol | wordmark | wordmark-tagline | stacked
 * @param {string} [opts.tone=color]      - color | white | blue | black | gold
 * @param {string|null} [opts.background]  - background fill (e.g. "#FFFFFF", SATIN_BLUE.deep) or null for transparent
 * @param {string} [opts.idPrefix=vp]      - unique gradient id prefix (avoid collisions when inlined)
 * @param {number|null} [opts.size]        - optional pixel width/height attributes (square uses height)
 * @param {boolean} [opts.roundedSquare]   - draw an app-icon style rounded background tile
 * @returns {string} SVG markup
 */
export function buildVoxPontSvg(opts = {}) {
  const {
    variant = "primary",
    tone = "color",
    background = null,
    idPrefix = "vp",
    roundedSquare = false,
  } = opts;

  const ink = resolveInk(tone, idPrefix);
  const needsGradients = tone === "color" || tone === "ondark";
  const cfg = VARIANTS[variant] || VARIANTS.primary;

  let body = "";

  if (variant === "symbol") {
    body = symbolGroup(ink);
  } else if (variant === "wordmark" || variant === "wordmark-tagline") {
    const size = 96;
    body =
      wordmarkGroup(ink, { x: 16, y: 104, size }) +
      (variant === "wordmark-tagline"
        ? taglineGroup(ink, { x: 20, y: 146, size: 18 })
        : "");
  } else if (variant === "stacked") {
    const size = 80;
    const symbolScale = 1.45;
    body =
      `<g transform="translate(${n((440 - SYM.w * symbolScale) / 2)}, 24) scale(${symbolScale})">${symbolGroup(ink)}</g>` +
      wordmarkGroup(ink, { x: 220, y: 290, size, anchor: "middle" }) +
      taglineGroup(ink, { x: 220, y: 332, size: 17, anchor: "middle" });
  } else {
    // primary / primary-no-tagline: symbol left, wordmark + tagline right.
    const size = 92;
    const wordX = 250;
    body =
      `<g transform="translate(20, 44) scale(1.05)">${symbolGroup(ink)}</g>` +
      wordmarkGroup(ink, { x: wordX, y: 130, size }) +
      (variant === "primary"
        ? taglineGroup(ink, { x: wordX + 4, y: 176, size: 19 })
        : "");
  }

  const [, , vbW, vbH] = cfg.viewBox.split(" ").map(Number);

  let bg = "";
  if (roundedSquare && background) {
    const r = Math.round(vbW * 0.2);
    bg = `<rect x="0" y="0" width="${vbW}" height="${vbH}" rx="${r}" ry="${r}" fill="${background}"/>`;
  } else if (background) {
    bg = `<rect x="0" y="0" width="${vbW}" height="${vbH}" fill="${background}"/>`;
  }

  const defs = needsGradients ? `<defs>${gradientDefs(idPrefix)}</defs>` : "";

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${cfg.viewBox}" role="img" aria-label="VoxPont">${defs}${bg}${body}</svg>`;
}

/**
 * App-icon lockup: centered symbol on a rounded tile, native 1024 grid.
 * @param {Object} opts
 * @param {string} [opts.tone=color]
 * @param {string} [opts.background] - tile fill (defaults to satin-blue deep)
 * @param {string} [opts.idPrefix=vpicon]
 */
export function buildAppIconSvg(opts = {}) {
  const { tone = "color", background = SATIN_BLUE.deep, idPrefix = "vpicon" } = opts;
  const ink = resolveInk(tone === "color" ? "color" : tone, idPrefix);
  const needsGradients = tone === "color";
  const size = 1024;
  const r = 224; // ~iOS superellipse-ish rounded square
  // Center the 200x120 symbol, scaled to ~620px wide.
  const scale = 3.1;
  const sw = SYM.w * scale;
  const sh = SYM.h * scale;
  const tx = (size - sw) / 2;
  const ty = (size - sh) / 2;
  const defs = needsGradients ? `<defs>${gradientDefs(idPrefix)}</defs>` : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" role="img" aria-label="VoxPont">${defs}<rect x="0" y="0" width="${size}" height="${size}" rx="${r}" ry="${r}" fill="${background}"/><g transform="translate(${n(tx)}, ${n(ty)}) scale(${scale})">${symbolGroup(ink)}</g></svg>`;
}

/**
 * Favicon: symbol only, tight square crop, transparent background.
 * @param {Object} opts
 * @param {string} [opts.tone=color]
 * @param {string} [opts.idPrefix=vpfav]
 */
export function buildFaviconSvg(opts = {}) {
  const { tone = "color", idPrefix = "vpfav" } = opts;
  const ink = resolveInk(tone, idPrefix);
  const needsGradients = tone === "color";
  const defs = needsGradients ? `<defs>${gradientDefs(idPrefix)}</defs>` : "";
  // Crop a little tighter than the native 200x120 box so the mark fills a square.
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="6 4 188 112" role="img" aria-label="VoxPont">${defs}${symbolGroup(ink)}</svg>`;
}
